import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Cookie, X, Check, Info, Settings, Lock, RotateCcw, Maximize2, Minimize2 } from 'lucide-react';

export interface CookieBannerProps {
  showBanner: boolean;
  onOpenPolicy: () => void;
  onAcceptAll: () => void;
  onAcceptEssential: () => void;
  onDismiss: () => void;
  floating?: boolean;
}

export const CookieBanner: React.FC<CookieBannerProps> = ({
  showBanner,
  onOpenPolicy,
  onAcceptAll,
  onAcceptEssential,
  onDismiss,
  floating = false,
}) => {
  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ opacity: 0, y: floating ? 24 : 14, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: floating ? 24 : 14, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 360, damping: 28 }}
          className={
            floating
              ? 'fixed bottom-3 sm:bottom-5 left-3 right-3 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 md:left-[calc(50vw+8rem)] lg:left-[calc(50vw+9rem)] md:-translate-x-1/2 w-auto sm:w-[calc(100%-2rem)] md:w-full md:max-w-2xl z-[85] select-none'
              : 'w-full max-w-2xl mx-auto mt-6 select-none relative z-20'
          }
        >
          <div className="relative p-2.5 sm:px-4 sm:py-2.5 rounded-xl sm:rounded-2xl bg-[#09090b]/95 border border-white/[0.1] hover:border-brand-500/30 shadow-[0_12px_36px_rgba(0,0,0,0.85),0_0_20px_rgba(245,158,11,0.06)] backdrop-blur-xl transition-all">
            {/* Subtle ambient rim light */}
            <div className="absolute inset-x-8 -top-px h-px bg-gradient-to-r from-transparent via-brand-500/35 to-transparent pointer-events-none" />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4">
              {/* Left: Icon & Notice */}
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <div className="p-1.5 rounded-lg bg-brand-500/10 border border-brand-500/20 text-brand-400 shrink-0">
                  <Cookie className="w-4 h-4" />
                </div>
                <p className="text-xs text-slate-200 font-medium leading-snug text-left">
                  This site uses cookies to remember your preferences.
                </p>
              </div>

              {/* Right: Actions Cluster */}
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 self-end sm:self-center">
                <button
                  onClick={onOpenPolicy}
                  className="text-[11px] font-medium text-slate-400 hover:text-white transition-colors cursor-pointer px-2 py-1"
                >
                  Policy
                </button>

                <button
                  onClick={onAcceptEssential}
                  className="px-2.5 py-1 text-xs font-medium text-slate-300 hover:text-white bg-neutral-900/90 hover:bg-neutral-800 border border-white/[0.08] rounded-lg transition-all cursor-pointer active:scale-95"
                >
                  Essential
                </button>

                <button
                  onClick={onAcceptAll}
                  className="px-3 py-1 text-xs font-semibold text-neutral-950 bg-gradient-to-r from-brand-500 to-amber-500 hover:from-brand-400 hover:to-amber-400 rounded-lg shadow-sm shadow-brand-500/25 transition-all cursor-pointer active:scale-95 flex items-center gap-1"
                >
                  <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                  <span>Accept</span>
                </button>

                <button
                  onClick={onDismiss}
                  className="p-1 text-slate-500 hover:text-slate-300 rounded-md transition-colors cursor-pointer ml-0.5"
                  title="Dismiss"
                  aria-label="Close cookie banner"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export interface CookiePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  consent: 'accepted' | 'essential' | null;
  onResetPreferences: () => void;
  onAcceptAll: () => void;
  onAcceptEssential: () => void;
}

export const CookiePolicyModal: React.FC<CookiePolicyModalProps> = ({
  isOpen,
  onClose,
  consent,
  onResetPreferences,
  onAcceptAll,
  onAcceptEssential,
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Close or exit full screen on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullScreen) {
          setIsFullScreen(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isFullScreen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="printable-modal-wrapper fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4 md:p-6 overflow-hidden">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="printable-modal-backdrop absolute inset-0 bg-black/85 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2 }}
            className={`printable-modal-card relative flex flex-col bg-[#0c0c0c] border border-neutral-800 shadow-2xl z-10 text-slate-300 transition-all duration-200 ${
              isFullScreen
                ? 'fixed inset-0 w-full h-full max-w-none max-h-none rounded-none border-0'
                : 'w-full max-w-4xl lg:max-w-5xl max-h-[90vh] rounded-2xl sm:rounded-3xl overflow-hidden m-2 sm:m-0'
            }`}
          >
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-neutral-900 flex justify-between items-center shrink-0 bg-neutral-950/95 sticky top-0 z-20 print-hide">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-500/10 text-brand-400 rounded-xl border border-brand-500/20">
                  <Cookie className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm sm:text-base text-white font-display flex items-center gap-2">
                    Cookie Policy
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      Zero Tracking
                    </span>
                  </h3>
                  <p className="text-[10px] sm:text-[11px] font-mono text-slate-500">Randon Brown, LLC | Technical Services</p>
                </div>
              </div>

              {/* Action Buttons: Pop Out Full Screen, Close */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  className="px-3 py-1.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 active:scale-95 shadow-sm"
                  title={isFullScreen ? "Exit Full Screen" : "Pop Out to Full Screen"}
                >
                  {isFullScreen ? (
                    <>
                      <Minimize2 className="w-3.5 h-3.5 text-brand-400" />
                      <span className="hidden sm:inline text-xs font-mono">Restore</span>
                    </>
                  ) : (
                    <>
                      <Maximize2 className="w-3.5 h-3.5 text-brand-400" />
                      <span className="hidden sm:inline text-xs font-mono">Pop Out</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 hover:bg-neutral-900 rounded-full transition-colors text-slate-400 hover:text-white cursor-pointer"
                  aria-label="Close Cookie Policy"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Official Print Header (Visible ONLY when printing) */}
            <div className="hidden print:block print-header">
              <h1 className="print-header-title">Randon Brown, LLC | Cookie Policy</h1>
              <p className="print-header-sub">Official Website &amp; Technical Services Cookie &amp; Storage Disclosures • randonbrown.com</p>
              <p className="print-header-sub">Effective Date: October 2026 • Contact: help@randonbrown.com • (314) 944-3074 • Texas County, MO</p>
            </div>

            {/* Scrollable Modal Body */}
            <div className="printable-modal-body p-5 sm:p-7 md:p-8 overflow-y-auto custom-scrollbar flex-1 space-y-4 text-xs sm:text-sm leading-relaxed text-left">
              {/* Summary Callout Banner */}
              <div className="p-4 rounded-2xl bg-brand-500/[0.07] border border-brand-500/20 flex items-start gap-3.5 print:border-gray-300 print-section-item">
                <Info className="w-5 h-5 text-brand-400 shrink-0 mt-0.5 print-hide" />
                <p className="text-slate-300 text-xs sm:text-sm leading-relaxed print-text">
                  This website is engineered with strict privacy principles. We do not use third-party tracking cookies, marketing beacons, behavioral profiling, or advertising networks. Storage is utilized solely for essential functional state.
                </p>
              </div>

              {/* Sections List */}
              <div className="space-y-4 pt-1">
                {/* Section 1 */}
                <div className="print-section-item p-4 sm:p-5 rounded-2xl bg-neutral-950/80 border border-neutral-900/90 hover:border-brand-500/30 transition-all space-y-2">
                  <div className="flex items-center gap-2.5">
                    <span className="w-6 h-6 rounded-lg bg-brand-500/10 text-brand-400 border border-brand-500/20 font-mono font-bold text-xs flex items-center justify-center shrink-0 print-hide">
                      1
                    </span>
                    <h4 className="font-bold text-sm sm:text-base text-white font-display print-title flex items-center gap-2">
                      <Info className="w-4 h-4 text-brand-400 print-hide" />
                      1. Overview &amp; Commitment to Privacy
                    </h4>
                  </div>
                  <div className="pl-0 sm:pl-8 text-slate-300 print-text">
                    <p>
                      This website (<span className="text-brand-400 font-mono">randonbrown.com</span>) is designed with user privacy and minimalism at its core. We do not deploy third-party advertising cookies, cross-site trackers, marketing beacons, or data broker integrations. Your visit remains private, and no behavioral dossiers are assembled.
                    </p>
                  </div>
                </div>

                {/* Section 2 */}
                <div className="print-section-item p-4 sm:p-5 rounded-2xl bg-neutral-950/80 border border-neutral-900/90 hover:border-brand-500/30 transition-all space-y-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-6 h-6 rounded-lg bg-brand-500/10 text-brand-400 border border-brand-500/20 font-mono font-bold text-xs flex items-center justify-center shrink-0 print-hide">
                      2
                    </span>
                    <h4 className="font-bold text-sm sm:text-base text-white font-display print-title flex items-center gap-2">
                      <Settings className="w-4 h-4 text-brand-400 print-hide" />
                      2. Data Storage Categories &amp; Purposes
                    </h4>
                  </div>

                  <div className="pl-0 sm:pl-8 space-y-3">
                    {/* Strictly Essential */}
                    <div className="p-3.5 rounded-xl bg-neutral-900/60 border border-neutral-800 space-y-1 print-section-item">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-white text-xs sm:text-sm print-title">Strictly Essential Storage</span>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold print-hide">
                          Always Active
                        </span>
                      </div>
                      <p className="text-slate-400 text-xs sm:text-sm leading-relaxed print-text">
                        Necessary for basic website security and remembering your cookie consent choice (<code className="text-brand-300 font-mono">rb_cookie_consent</code>) so you aren't prompted repeatedly on every page load.
                      </p>
                    </div>

                    {/* Functional Preferences */}
                    <div className="p-3.5 rounded-xl bg-neutral-900/60 border border-neutral-800 space-y-1 print-section-item">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-white text-xs sm:text-sm print-title">Functional Preferences</span>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand-500/10 text-brand-400 border border-brand-500/20 font-bold print-hide">
                          Functional
                        </span>
                      </div>
                      <p className="text-slate-400 text-xs sm:text-sm leading-relaxed print-text">
                        Remembers your voluntary sound effects toggle, active navigation tab selection, and local interactive sandbox states (such as your interactive RJ45 cable termination trainer progress).
                      </p>
                    </div>

                    {/* Marketing & Tracking (None) */}
                    <div className="p-3.5 rounded-xl bg-neutral-900/30 border border-neutral-900 space-y-1 opacity-75 print-section-item">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-300 text-xs sm:text-sm print-title">Marketing &amp; Ad Trackers</span>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-neutral-800 text-slate-400 border border-neutral-700 font-bold print-hide">
                          Disabled / None
                        </span>
                      </div>
                      <p className="text-slate-400 text-xs sm:text-sm leading-relaxed print-text">
                        We do not deploy Facebook Pixel, Google Ads remarketing, fingerprinting scripts, or any commercial profiling tools.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section 3 */}
                <div className="print-section-item p-4 sm:p-5 rounded-2xl bg-neutral-950/80 border border-neutral-900/90 hover:border-brand-500/30 transition-all space-y-2">
                  <div className="flex items-center gap-2.5">
                    <span className="w-6 h-6 rounded-lg bg-brand-500/10 text-brand-400 border border-brand-500/20 font-mono font-bold text-xs flex items-center justify-center shrink-0 print-hide">
                      3
                    </span>
                    <h4 className="font-bold text-sm sm:text-base text-white font-display print-title flex items-center gap-2">
                      <Lock className="w-4 h-4 text-brand-400 print-hide" />
                      3. External Portals &amp; Secure Integrations
                    </h4>
                  </div>
                  <div className="pl-0 sm:pl-8 text-slate-300 space-y-2 print-text">
                    <p>
                      When accessing external services—including the Client Billing Portal hosted on Zoho (<span className="text-brand-400 font-mono">invoice.zohosecure.com</span>), LinkedIn, or YouTube—those respective platforms maintain their own independent cookie policies and terms.
                    </p>
                    <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                      Form submissions (Service Quotes and Website Onboarding) transmit encrypted data directly to our Google Cloud Firestore database and dispatch transactional email receipts via Resend API (<span className="text-brand-300 font-mono">help@randonbrown.com</span>) without injecting third-party advertising trackers.
                    </p>
                  </div>
                </div>

                {/* Section 4: Current Saved Preferences & Cache Purge (Interactive in UI) */}
                <div className="p-4 rounded-2xl bg-neutral-950 border border-neutral-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs print-hide">
                  <div>
                    <span className="text-slate-400">Current Saved Preference: </span>
                    <span className="font-mono font-bold text-brand-400 uppercase">
                      {consent ? consent : 'Not Set'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={onResetPreferences}
                      className="text-slate-400 hover:text-white underline cursor-pointer text-xs"
                    >
                      Reset Preferences
                    </button>
                    <span className="text-neutral-700">&bull;</span>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          if ('caches' in window) {
                            const names = await window.caches.keys();
                            await Promise.all(names.map(name => window.caches.delete(name)));
                          }
                          if ('serviceWorker' in navigator) {
                            const regs = await navigator.serviceWorker.getRegistrations();
                            await Promise.all(regs.map(r => r.unregister()));
                          }
                          sessionStorage.clear();
                          localStorage.clear();
                          window.location.reload();
                        } catch {
                          window.location.reload();
                        }
                      }}
                      className="text-brand-400 hover:text-brand-300 flex items-center gap-1 cursor-pointer text-xs font-semibold"
                      title="Purge all site storage, caches, and reload"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Clear All Cache</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer Controls */}
            <div className="p-4 sm:p-5 bg-neutral-950/95 border-t border-neutral-900 flex flex-wrap items-center justify-between gap-3 shrink-0 print-hide">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    onAcceptAll();
                    onClose();
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-brand-500 to-amber-600 hover:from-brand-400 hover:to-amber-500 text-neutral-950 text-xs font-bold rounded-xl transition-all cursor-pointer shadow-sm active:scale-95"
                >
                  Accept All
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onAcceptEssential();
                    onClose();
                  }}
                  className="px-4 py-2 bg-neutral-900 hover:bg-neutral-800 text-slate-300 text-xs font-semibold rounded-xl border border-neutral-800 transition-colors cursor-pointer active:scale-95"
                >
                  Essential Only
                </button>
              </div>

              <div className="flex items-center gap-2 ml-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export function useCookieConsent() {
  const [consent, setConsent] = useState<'accepted' | 'essential' | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('rb_cookie_consent');
      if (saved === 'accepted' || saved === 'essential') {
        setConsent(saved as 'accepted' | 'essential');
      } else {
        const timer = setTimeout(() => {
          setShowBanner(true);
        }, 600);
        return () => clearTimeout(timer);
      }
    } catch {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    try {
      localStorage.setItem('rb_cookie_consent', 'accepted');
    } catch {}
    setConsent('accepted');
    setShowBanner(false);
  };

  const acceptEssential = () => {
    try {
      localStorage.setItem('rb_cookie_consent', 'essential');
    } catch {}
    setConsent('essential');
    setShowBanner(false);
  };

  const resetPreferences = () => {
    try {
      localStorage.removeItem('rb_cookie_consent');
    } catch {}
    setConsent(null);
    setShowBanner(true);
  };

  return {
    consent,
    showBanner,
    setShowBanner,
    acceptAll,
    acceptEssential,
    resetPreferences,
  };
}

export interface CookieConsentProps {
  isPolicyOpen: boolean;
  onOpenPolicy: () => void;
  onClosePolicy: () => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({
  isPolicyOpen,
  onOpenPolicy,
  onClosePolicy,
}) => {
  const { consent, showBanner, acceptAll, acceptEssential, resetPreferences } = useCookieConsent();

  return (
    <>
      <CookieBanner
        showBanner={showBanner}
        floating={true}
        onOpenPolicy={onOpenPolicy}
        onAcceptAll={acceptAll}
        onAcceptEssential={acceptEssential}
        onDismiss={acceptEssential}
      />
      <CookiePolicyModal
        isOpen={isPolicyOpen}
        onClose={onClosePolicy}
        consent={consent}
        onResetPreferences={resetPreferences}
        onAcceptAll={acceptAll}
        onAcceptEssential={acceptEssential}
      />
    </>
  );
};

export default CookieConsent;
